<template>
    <div class="z-40">
      <div class=" w-full h-auto mx-auto">
          <div class="relative w-full">
              <CloseButton class="absolute !-top-1 !-right-1" @close="close()"/>
              <div class="flex w-full justify-center text-center">
                  <BodyInfo :song="song" :artist="artist" />
              </div>
              <div class="flex flex-col items-center w-full justify-center text-center">
                <AudioRange @move="onMove" :time="currentTime" />
                <div class="flex flex-row items-center justify-between text-xs text-gray-500 w-full text-center pl-2 pr-2">
                  <span>{{ formatTime }}</span> <span>{{ formatDuration }}</span>
                </div>
              </div>
              <div class="flex justify-center w-full z-20">
                <PlayerControls :isPaused="isPaused" @play="playSong" @previous="previousSong" @next="nextSong" />
              </div>
              <!-- Keep it-->
              <div class="hidden relative md:w-[500px] md:h-[300px] transition-all absolute -top-4"
              :style="image"
              ref="cover" v-if="videoId">
                  <Transition>
                      <SafeYoutube class="z-40" v-if="videoId" ref="youtube" 
                      :videoid="videoId" :controls="1" :autoplay="0" :width="500" :height="300" 
                      @ended="onEnded" @paused="onPaused" @played="onPlayed"/>
                  </Transition>
              </div>
          </div>
      </div>
  </div>
</template>
<script setup>
import {defineProps, onMounted, onUnmounted, ref, watch, defineEmits, nextTick, computed} from 'vue';
import SafeYoutube from './common/SafeYoutube.vue';
import PlayerControls from './common/elements/PlayerControls.vue';
import BodyInfo from './common/BodyInfo.vue';
import { appEvents } from '@/store/app.events';

const emits = defineEmits(['close', 'next', 'previous'])
const props = defineProps(["url", "metadata", "selectedMedia"])
const url = ref(props.url)
const videoId = ref(null)
const youtube = ref('youtube');
const isPaused = ref(true);
const metadata = ref(props.metadata)
const currentTime = ref(0)
const duration = ref(0);
const cover = ref(null);
const currentTimeSeconds = ref(0);

const pauseVideo = () => {
  if (youtube.value && youtube.value.player && !isPaused.value) {
    youtube.value.player.pauseVideo();
    isPaused.value = true;
  }
}

onMounted( () => {
    url.value = props.url;
    videoId.value = getVideoId();
    if (!videoId.value) {
      url.value = props.url.replace("watch?v=", 'embed/');
      videoId.value = url.value.replace("https://", "").replace("www.", "").replace("youtube.com/embed/", "");
    }
    metadata.value = props.metadata;
    setTimeout( async () => {
      if (youtube.value && youtube.value.player){
        duration.value = await youtube.value.player.getDuration()
      }
    },250)
    appEvents.on('pause-main-player', pauseVideo);
})

onUnmounted(() => {
    appEvents.off('pause-main-player', pauseVideo);
})

watch( () => props.url, () => {
    isPaused.value = true;
    if (youtube.value && youtube.value.player){
      youtube.value.player.pauseVideo()
      setTimeout( async () => {
        if (youtube.value && youtube.value.player){
          duration.value = await youtube.value.player.getDuration()
        }
      },250)
    }
    url.value = props.url.replace("watch?v=", 'embed/');
    url.value = props.url;
    videoId.value = getVideoId();
    if (!videoId.value) {
      url.value = props.url.replace("watch?v=", 'embed/');
      videoId.value = url.value.replace("https://", "").replace("www.", "").replace("youtube.com/embed/", "");
    }
});

const getVideoId = () => {
  try{
    const searchParams = new URLSearchParams(url.value);
    const videoId = searchParams.get('v');
    if (videoId) {
      return videoId;
    }
    return Array.from(searchParams.entries())[0][1];
  } catch (error) {
    console.error("Error getting video id", error);
    return null;
  }
}
let timer
const getCurrentTime = async () => {
  if (youtube.value && youtube.value.player){
    let youtubeCurrentTime = await youtube.value.player.getCurrentTime();
    currentTimeSeconds.value = youtubeCurrentTime;
    if (!duration.value){
      duration.value = await youtube.value.player.getDuration()
    }
    currentTime.value = (youtubeCurrentTime/duration.value)*100
    if (isNaN(currentTime.value) || !isFinite(currentTime.value)) {
      currentTime.value = 0;
    }
    if (!isPaused.value) {
      timer = setTimeout( () => {
        getCurrentTime()
      } , 500);
    } else {
      clearTimeout(timer);
    }
  } else {
    clearTimeout(timer);
  }
}

const formatTime = computed(() => {
  const minutes = Math.floor((currentTimeSeconds.value % 3600) / 60);
  const seconds = Math.floor(currentTimeSeconds.value % 60);

  const currentString = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  return currentString;
})

const formatDuration = computed(() => {
  const minutes = Math.floor((duration.value % 3600) / 60);
  const seconds = Math.floor(duration.value % 60);
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
})

const nextSong = () => {
  emits('next');
  duration.value = 0;
  currentTime.value=0;
  if (youtube.value.player) {
    youtube.value.player.seekTo(0, true)
  }
}
const previousSong = () => {
  emits('previous')
  duration.value = 0;
  currentTime.value=0;
  if (youtube.value.player) {
    youtube.value.player.seekTo(0, true)
  }
}
const playSong = () => {
  if (isPaused.value){
    youtube.value.player.playVideo();
  } else {
    youtube.value.player.pauseVideo();
  }
}
const close = () => {
  if(youtube.value && youtube.value.player){
    youtube.value.player.stopVideo();
  }
  duration.value=0;
  currentTime.value=0;
  videoId.value = null;
  emits('close');
}
const onEnded = () => {
  isPaused.value = true;
  currentTime.value=0;
}
const onPaused= () => {
  isPaused.value = true;
}
const onPlayed= () => {
  isPaused.value = false;
  appEvents.emit('pause-side-panel-player');
  nextTick( () => {
    getCurrentTime();
  })
}

const onMove = (e) => {
    if(youtube.value && youtube.value.player){
      youtube.value.player.seekTo(duration.value*(e.percentage/100), true)
    }
}


const image = computed( () => {
    let value = null
    if (props.selectedMedia && props.selectedMedia.images && props.selectedMedia.images.length > 0) {
      value = props.selectedMedia.images.filter(i => i != null && i != "")[0];
    }
    if (videoId.value){
      value = `https://i.ytimg.com/vi/${videoId.value}/sddefault.jpg`;
    } 
    if (value) {
      value = `background-image: url("${value}");background-position: center;
    background-position-x: center;
  background-position-x: 0;
  background-position-y: center;
  background-size: cover;
  background-repeat: no-repeat;`
    }
    return value;
})

const song = computed(() => {
  if (metadata.value && metadata.value.data && metadata.value.data.title) {
    return metadata.value.data.title;
  } else {
    return "-";
  }
});
const artist = computed(() => {
  return metadata.value?.data?.artist?.name || (typeof metadata.value?.data?.artist === 'string' ? metadata.value.data.artist : '-');
});
</script>